import { EMAIL_FORMAT } from "@shared/formats/email";
import { PHONE_FORMAT } from "@shared/formats/phone";
import {
  isAddressFieldBlank,
  isRequestDeliveryAddressComplete,
} from "@shared/validation/address";
import { parsePositiveQuantity } from "@shared/validation/positive-number";
import type {
  GuestContactState,
  QuoteContactMode,
  QuoteFormState,
  QuoteTouchedFields,
} from "./listing-quote-request-state";

export const QUOTE_REQUIREMENT_FIELDS = [
  "quantity",
  "deliveryAddress",
  "fulfillmentType",
  "preferredDate",
  "name",
  "email",
  "phone",
] as const;

export type QuoteRequirementField = (typeof QUOTE_REQUIREMENT_FIELDS)[number];

export type QuoteRequirementErrors = Partial<
  Record<QuoteRequirementField, string>
>;

interface SubmitRequirement {
  field: QuoteRequirementField;
  message: string;
}

export interface QuoteValidity {
  canSubmit: boolean;
  blockedByField: QuoteRequirementField | null;
  errors: QuoteRequirementErrors;
}

export function deriveFormValidity(
  state: QuoteFormState,
  pickupAvailable: boolean,
  contactMode: QuoteContactMode,
): QuoteValidity {
  const requirements = [
    ...getOrderRequirements(state, pickupAvailable),
    ...getContactRequirements(state.contact, contactMode),
  ];
  return {
    canSubmit: requirements.length === 0,
    blockedByField: requirements[0]?.field ?? null,
    errors: Object.fromEntries(
      requirements.map(({ field, message }) => [field, message]),
    ),
  };
}

export function pickTouchedErrors(
  errors: QuoteRequirementErrors,
  touched: QuoteTouchedFields,
): QuoteRequirementErrors {
  const shown: QuoteRequirementErrors = {};
  for (const field of QUOTE_REQUIREMENT_FIELDS) {
    const message = errors[field];
    if (touched[field] && message) shown[field] = message;
  }
  return shown;
}

export function everyRequirementTouched(): QuoteTouchedFields {
  return Object.fromEntries(
    QUOTE_REQUIREMENT_FIELDS.map((field) => [field, true]),
  );
}

export function isStreetUnpicked(address: QuoteFormState["deliveryAddress"]): boolean {
  return (
    !isAddressFieldBlank(address.line1) &&
    isAddressFieldBlank(address.city) &&
    isAddressFieldBlank(address.state) &&
    isAddressFieldBlank(address.postalCode)
  );
}

function deliveryAddressMessage(
  address: QuoteFormState["deliveryAddress"],
): string {
  return isStreetUnpicked(address)
    ? "Pick a suggested address, or enter the city, state and ZIP."
    : "Enter a complete delivery address.";
}

function getOrderRequirements(
  state: QuoteFormState,
  pickupAvailable: boolean,
): SubmitRequirement[] {
  const requirements: SubmitRequirement[] = [];
  if (parsePositiveQuantity(state.quantityDisplay) === null) {
    requirements.push({
      field: "quantity",
      message: "Enter how many units you need.",
    });
  }
  if (
    state.fulfillmentType === "delivery" &&
    !isRequestDeliveryAddressComplete(state.deliveryAddress)
  ) {
    requirements.push({
      field: "deliveryAddress",
      message: deliveryAddressMessage(state.deliveryAddress),
    });
  }
  if (state.fulfillmentType === "pickup" && !pickupAvailable) {
    requirements.push({
      field: "fulfillmentType",
      message: "Pickup is unavailable for this listing. Choose delivery instead.",
    });
  }
  if (!state.preferredDate) {
    requirements.push({
      field: "preferredDate",
      message: `Choose a preferred ${state.fulfillmentType} date.`,
    });
  }
  return requirements;
}

function getContactRequirements(
  contact: GuestContactState,
  contactMode: QuoteContactMode,
): SubmitRequirement[] {
  if (contactMode === "account") return [];
  const requirements: SubmitRequirement[] = [];
  if (!contact.name.trim()) {
    requirements.push({ field: "name", message: "Enter your name." });
  }
  if (contactMode === "guest" && !EMAIL_FORMAT.validate(contact.email)) {
    requirements.push({ field: "email", message: EMAIL_FORMAT.errorMessage });
  }
  if (!PHONE_FORMAT.validate(contact.phone)) {
    requirements.push({
      field: "phone",
      message: PHONE_FORMAT.diagnose(contact.phone) ?? PHONE_FORMAT.errorMessage,
    });
  }
  return requirements;
}

export function focusQuoteRequirement(
  form: HTMLFormElement | null,
  field: QuoteRequirementField,
) {
  const container = form?.querySelector<HTMLElement>(
    `[data-quote-requirement="${field}"]`,
  );
  const target =
    container?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
    container?.querySelector<HTMLElement>("input, button, select, textarea");
  target?.focus();
}
